import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminService } from '@/services/admin.service';
import { useToast } from '@/contexts/ToastContext';
import type { AdminSeason, SeasonRankingEntry } from '@/types/admin';

const AdminSeasonRankingPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [season, setSeason] = useState<AdminSeason | null>(null);
  const [rankings, setRankings] = useState<SeasonRankingEntry[]>([]);
  const [frozen, setFrozen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) loadData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    if (!id) return;
    try {
      const [seasonData, rankingData] = await Promise.all([
        adminService.getSeason(id),
        adminService.getSeasonRankings(id),
      ]);
      setSeason(seasonData);
      setRankings(rankingData.rankings);
      setFrozen(rankingData.frozen);
    } catch {
      showToast('Erro ao carregar dados', 'error');
      navigate('/admin/temporadas');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="p-10 text-content-muted">Carregando...</div>;
  }

  return (
    <div className="p-6 lg:p-10">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/admin/temporadas')} className="text-content-muted hover:text-content-primary">
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <div>
          <h1 className="text-2xl font-bold">Ranking — {season?.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            {frozen ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400">
                <span className="material-symbols-outlined text-sm">lock</span>
                Ranking Congelado
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                <span className="material-symbols-outlined text-sm">wifi</span>
                Ranking ao Vivo
              </span>
            )}
          </div>
        </div>
      </div>

      {rankings.length === 0 ? (
        <p className="text-content-muted">Nenhum dado de ranking disponível.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-theme-border text-left text-content-muted text-sm">
                <th className="pb-3 font-medium w-16 text-center">#</th>
                <th className="pb-3 font-medium">Nome</th>
                <th className="pb-3 font-medium text-center">Level</th>
                <th className="pb-3 font-medium text-center">XP Total</th>
                <th className="pb-3 font-medium">Setor</th>
                <th className="pb-3 font-medium text-center">Pos. Setor</th>
              </tr>
            </thead>
            <tbody>
              {rankings.map((entry) => (
                <tr key={entry.user_id} className="border-b border-theme-border/50 hover:bg-surface-hover/50">
                  <td className="py-3 text-center">
                    <span className={`font-bold ${
                      entry.overall_rank === 1 ? 'text-yellow-400' :
                      entry.overall_rank === 2 ? 'text-gray-300' :
                      entry.overall_rank === 3 ? 'text-amber-600' : 'text-content-secondary'
                    }`}>
                      {entry.overall_rank}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center gap-3">
                      {entry.avatar ? (
                        <img src={entry.avatar} alt="" className="size-8 rounded-full object-cover" />
                      ) : (
                        <div className="size-8 rounded-full bg-accent/10 flex items-center justify-center">
                          <span className="material-symbols-outlined text-sm text-accent">person</span>
                        </div>
                      )}
                      <span className="font-medium">{entry.name}</span>
                    </div>
                  </td>
                  <td className="py-3 text-center">{entry.level}</td>
                  <td className="py-3 text-center font-medium">{entry.overall_xp.toLocaleString('pt-BR')}</td>
                  <td className="py-3 text-sm text-content-secondary">{entry.sector_name || '—'}</td>
                  <td className="py-3 text-center text-sm">
                    {entry.sector_rank ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminSeasonRankingPage;
